import { jsx as _jsx } from "react/jsx-runtime";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Skeleton from "@mui/material/Skeleton";
import Alert from "@mui/material/Alert";
import { alpha } from "@mui/material/styles";
import EventBusyRoundedIcon from "@mui/icons-material/EventBusyRounded";
import { useTranslation } from "../../hooks";
import { PortalEmptyState } from "./ui/PortalEmptyState";
function timeLabel(value) {
    if (!value)
        return "";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime()))
        return "";
    return parsed.toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit",
    });
}
const GRID_SX = {
    display: "grid",
    gridTemplateColumns: {
        xs: "repeat(auto-fill, minmax(84px, 1fr))",
        sm: "repeat(auto-fill, minmax(96px, 1fr))",
    },
    gap: 1,
};
export function SlotGrid({ slots, selectedIndex, onSelect, loading, error, onRetry, disabled, }) {
    const { t } = useTranslation("portal");
    if (loading) {
        return (_jsx(Box, { sx: GRID_SX, "aria-busy": "true", children: Array.from({ length: 8 }, (_, index) => (_jsx(Skeleton, { variant: "rounded", height: 40 }, index))) }));
    }
    if (error) {
        return (_jsx(Alert, { severity: "error", sx: { borderRadius: 2 }, action: _jsx(Button, { size: "small", onClick: onRetry, children: t("retry", "Try again") }), children: t("slotsFailed", "We couldn't load times for that day.") }));
    }
    if (slots.length === 0) {
        return (_jsx(Box, { sx: (theme) => ({
                borderRadius: 2,
                border: `1px dashed ${theme.palette.divider}`,
            }), children: _jsx(PortalEmptyState, { icon: _jsx(EventBusyRoundedIcon, {}), title: t("noTimes", "No times available on this day."), hint: t("noTimesHint", "Try another day on the strip above.") }) }));
    }
    return (_jsx(Box, { sx: GRID_SX, children: slots.map((slot, index) => {
            const selected = selectedIndex === index;
            return (_jsx(ButtonBase, { disabled: disabled, "aria-pressed": selected, onClick: () => onSelect(index), sx: (theme) => ({
                    py: 1,
                    px: 1,
                    borderRadius: 2,
                    fontSize: 13,
                    fontWeight: 600,
                    border: `1px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
                    color: selected
                        ? theme.palette.primary.contrastText
                        : theme.palette.text.primary,
                    backgroundColor: selected
                        ? theme.palette.primary.main
                        : theme.palette.background.paper,
                    transition: "background-color 140ms ease, border-color 140ms ease",
                    "&:hover": {
                        borderColor: theme.palette.primary.main,
                        backgroundColor: selected
                            ? theme.palette.primary.dark
                            : alpha(theme.palette.primary.main, 0.06),
                    },
                    "&.Mui-disabled": { opacity: 0.5 },
                    "&:focus-visible": {
                        outline: `2px solid ${theme.palette.primary.main}`,
                        outlineOffset: 2,
                    },
                    "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                }), children: timeLabel(slot.fromDateTimeIso ?? slot.fromDateTimeTs) }, String(slot.fromDateTimeTs ?? slot.fromDateTimeIso ?? index)));
        }) }));
}
